import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";

/** The clipboard where the browser offers it, else a focused text box, so Copy works everywhere. */
export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Why: an unfocused or embedded page refuses the clipboard; the text box below still copies.
    }
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy command was rejected");
}

// The owner's backend posts sales here: the API on the app's own address.
const CONVERSION_ENDPOINT = `${window.location.origin}/api/track/conversion`;
export const KEY_PLACEHOLDER = "YOUR_KEY";

export type ExampleTab = "curl" | "node" | "stripe";

export const EXAMPLE_TABS: { id: ExampleTab; label: string }[] = [
  { id: "curl", label: "curl" },
  { id: "node", label: "Node" },
  { id: "stripe", label: "Stripe webhook" },
];

export function exampleFor(tab: ExampleTab, key: string): string {
  if (tab === "curl") {
    return (
      `curl -X POST ${CONVERSION_ENDPOINT} \\\n` +
      `  -H "Authorization: Bearer ${key}" \\\n` +
      `  -H "Content-Type: application/json" \\\n` +
      `  -d '{"orderId":"order_123","amountCents":15000,"coupon":"SARAH15"}'`
    );
  }
  if (tab === "node") {
    return (
      `await fetch("${CONVERSION_ENDPOINT}", {\n` +
      `  method: "POST",\n` +
      `  headers: {\n` +
      `    "Authorization": "Bearer ${key}",\n` +
      `    "Content-Type": "application/json",\n` +
      `  },\n` +
      `  body: JSON.stringify({\n` +
      `    orderId: order.id,\n` +
      `    amountCents: order.totalCents,\n` +
      `    coupon: order.couponCode,\n` +
      `  }),\n` +
      `});`
    );
  }
  return (
    `// In your Stripe webhook handler\n` +
    `if (event.type === "checkout.session.completed") {\n` +
    `  const session = event.data.object;\n` +
    `  await fetch("${CONVERSION_ENDPOINT}", {\n` +
    `    method: "POST",\n` +
    `    headers: {\n` +
    `      "Authorization": "Bearer ${key}",\n` +
    `      "Content-Type": "application/json",\n` +
    `    },\n` +
    `    body: JSON.stringify({\n` +
    `      orderId: session.id,\n` +
    `      amountCents: session.amount_total,\n` +
    `      coupon: session.discounts?.[0]?.promotion_code,\n` +
    `    }),\n` +
    `  });\n` +
    `}`
  );
}

/**
 * Confirming sales for partners, for the whole account: the read-only Stripe key, or the secret key a
 * checkout posts each sale with. The raw secret key comes back once, after Generate, and is held only
 * in memory until the page reloads.
 */
export function useSalesTracking(say: (message: string, done?: boolean) => void) {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [keySet, setKeySet] = useState(false);
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeKeyHint, setStripeKeyHint] = useState<string | null>(null);
  const [stripeKey, setStripeKey] = useState("");
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeErr, setStripeErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.connections
      .tracking()
      .then((d) => {
        if (!alive) return;
        setKeySet(d.tracking.conversionKeySet);
        setKeyPrefix(d.tracking.conversionKeyPrefix);
        setStripeConnected(d.tracking.stripeConnected);
        setStripeKeyHint(d.tracking.stripeKeyHint);
      })
      .catch((e: unknown) => alive && setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  /** True once the key was taken: the sheet that asked for it can close. */
  async function connectStripe(): Promise<boolean> {
    const key = stripeKey.trim();
    if (!key) return false;
    setStripeBusy(true);
    setStripeErr(null);
    try {
      const res = await api.connections.stripeConnect(key);
      setStripeConnected(res.tracking.stripeConnected);
      setStripeKeyHint(res.tracking.stripeKeyHint);
      setStripeKey("");
      say("Stripe connected");
      return true;
    } catch (e) {
      setStripeErr(e instanceof ApiError ? e.message : "Could not connect Stripe");
      return false;
    } finally {
      setStripeBusy(false);
    }
  }

  async function disconnectStripe() {
    setStripeBusy(true);
    try {
      const res = await api.connections.stripeDisconnect();
      setStripeConnected(res.tracking.stripeConnected);
      setStripeKeyHint(res.tracking.stripeKeyHint);
      say("Stripe disconnected");
    } catch (e) {
      say(e instanceof ApiError ? e.message : "Could not disconnect Stripe", false);
    } finally {
      setStripeBusy(false);
    }
  }

  // Generate a first key, or rotate (which invalidates the old one). Both return the raw key once.
  async function generateKey(rotate: boolean) {
    if (keyBusy) return;
    setKeyBusy(true);
    try {
      const res = await api.connections.generateConversionKey();
      setKeySet(res.tracking.conversionKeySet);
      setKeyPrefix(res.tracking.conversionKeyPrefix);
      setFreshKey(res.key);
      setKeyRevealed(true);
      say(rotate ? "New key ready. Copy it now" : "Key ready. Copy it now");
    } catch (e) {
      say(e instanceof ApiError ? e.message : "Couldn't create the key", false);
    } finally {
      setKeyBusy(false);
    }
  }

  return {
    loaded,
    err,
    keySet,
    freshKey,
    keyRevealed,
    setKeyRevealed,
    keyBusy,
    maskedKey: keyPrefix ? `${keyPrefix}${"•".repeat(16)}` : null,
    stripeConnected,
    stripeKeyHint,
    stripeKey,
    setStripeKey,
    stripeBusy,
    stripeErr,
    setStripeErr,
    connectStripe,
    disconnectStripe,
    generateKey,
  };
}

export type SalesTracking = ReturnType<typeof useSalesTracking>;
